// InboxItemPage (`/me/inbox/:kind/:id`, usado por deep link de e-mail — M1 §5): detalhe por tipo de item.
// `kind` fora do conjunto fechado (URL malformada) cai no NotFound local sem chamar a API; um item que a API
// não encontra (removido, id inválido, ou fora do escopo do chamador) chega como `404 PLT_NOT_FOUND` e é
// mostrado pelo `ProblemAlert` (`isOutOfScope`), o mesmo tratamento de qualquer 404 de consulta (Parte 14.5).
import { Button, Skeleton } from '@giyus/ui';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { ProblemAlert } from '../../components';
import { NotFoundPage } from '../../pages/NotFound';
import { useInboxItem } from './api/queries';
import { ApprovalDetail } from './components/ApprovalDetail';
import { ExceptionDetail } from './components/ExceptionDetail';
import { TaskDetail } from './components/TaskDetail';
import type { WorkItemKind } from './api/contract.preview';

const VALID_KINDS: readonly WorkItemKind[] = ['task', 'approval', 'exception'];

export function InboxItemPage() {
  const { t } = useTranslation('workflow');
  const { kind, id } = useParams<{ kind: string; id: string }>();
  const validKind = VALID_KINDS.find((k) => k === kind);
  const { data, isLoading, isError, error, refetch } = useInboxItem(validKind ?? 'task', id ?? '');

  if (!validKind || !id) {
    return <NotFoundPage />;
  }
  if (isError) {
    return (
      <ProblemAlert
        error={error}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }
  if (isLoading || !data) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <section className="flex flex-col gap-4" data-testid="inbox-item-page">
      <Button asChild variant="outline" className="self-start min-h-(--control-h-sm)">
        <Link to="/me/inbox">{t('actions.viewAll')}</Link>
      </Button>
      {data.kind === 'task' && <TaskDetail task={data} />}
      {data.kind === 'approval' && <ApprovalDetail approval={data} />}
      {data.kind === 'exception' && <ExceptionDetail exception={data} />}
    </section>
  );
}
